import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { Target as TargetIcon, Download } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const METRIC_LABELS = { accounts: "Accounts", shipments: "Shipments", revenue: "Revenue" };

function fmtMonthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  try { return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" }); }
  catch { return m; }
}

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => {
    const v = r[c];
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function TargetAchievement() {
  const [month, setMonth] = useState(thisMonth());
  const [agentId, setAgentId] = useState("");
  const [clientType, setClientType] = useState("");
  const [opts, setOpts] = useState({ agents: [], client_types: [], months: [], metric_types: [] });
  const [data, setData] = useState({ rows: [], totals: [], metric_types: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/targets/filter-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (agentId) params.set("agent_id", agentId);
      if (clientType) params.set("client_type", clientType);
      const { data: d } = await api.get(`/targets/summary?${params.toString()}`);
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month, agentId, clientType]);

  // Pivot per-agent rows to: { agent_name, accounts_target, accounts_achieved, ... }
  const byAgent = {};
  (data.rows || []).forEach((r) => {
    byAgent[r.agent_id] = byAgent[r.agent_id] || { agent_id: r.agent_id, agent_name: r.agent_name };
    byAgent[r.agent_id][`${r.metric_type}_target`] = r.target;
    byAgent[r.agent_id][`${r.metric_type}_achieved`] = r.achieved;
    byAgent[r.agent_id][`${r.metric_type}_pct`] = r.achievement_pct;
  });
  const agentRows = Object.values(byAgent);
  const metrics = data.metric_types?.length ? data.metric_types : ["accounts", "shipments", "revenue"];

  return (
    <Layout title="Target vs Achievement" breadcrumb={["Reports", "Targets"]}>
      {/* Filter row */}
      <div className="mb-5 bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-wrap items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1 inline-flex items-center gap-1.5">
          <TargetIcon size={12} /> Filters
        </div>
        <select data-testid="ta-month" value={month} onChange={(e) => setMonth(e.target.value)} className="text-sm px-3 py-1.5 border border-slate-200 rounded-md bg-white">
          {(opts.months || []).map((m) => <option key={m} value={m}>{fmtMonthLabel(m)}</option>)}
        </select>
        <select data-testid="ta-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="text-sm px-3 py-1.5 border border-slate-200 rounded-md bg-white">
          <option value="">All agents</option>
          {(opts.agents || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select data-testid="ta-client-type" value={clientType} onChange={(e) => setClientType(e.target.value)} className="text-sm px-3 py-1.5 border border-slate-200 rounded-md bg-white">
          <option value="">All client types</option>
          {(opts.client_types || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => downloadCsv("target-vs-achievement", agentRows)} className="ml-auto text-xs font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5">
          <Download size={12} /> Download CSV
        </button>
      </div>

      {/* KPI cards — overall totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        {metrics.map((m, i) => {
          const t = (data.totals || []).find((x) => x.metric_type === m);
          if (!t) return null;
          const pct = t.achievement_pct;
          const color = pct === null ? "slate" : pct >= 100 ? "emerald" : pct >= 75 ? "amber" : "rose";
          return (
            <div key={m} className={`bg-white border border-${color}-200 rounded-xl p-5 shadow-sm`} data-testid={`kpi-${m}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{METRIC_LABELS[m] || m}</div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-2xl font-bold text-slate-900">{Number(t.achieved).toLocaleString()}</span>
                <span className="text-slate-400">/ {Number(t.target).toLocaleString()}</span>
              </div>
              <div className={`text-xs font-semibold mt-1 text-${color}-700`}>
                {pct === null ? "No target set" : `${pct}% achieved`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-metric bar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {metrics.map((m) => {
          const chartRows = agentRows.map((r) => ({
            name: r.agent_name,
            target: r[`${m}_target`] || 0,
            achieved: r[`${m}_achieved`] || 0,
          })).filter((r) => r.target > 0 || r.achieved > 0);
          return (
            <div key={m} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900 mb-2">{METRIC_LABELS[m] || m}</h3>
              <div className="h-56">
                {chartRows.length === 0 ? (
                  <div className="h-full grid place-items-center text-sm text-slate-500">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartRows}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="target" fill="#cbd5e1" radius={[6, 6, 0, 0]} name="Target" />
                      <Bar dataKey="achieved" fill="#f97316" radius={[6, 6, 0, 0]} name="Achieved" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed agent table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Agent-level detail</h3>
          {loading && <span className="text-xs text-slate-500">Loading…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th rowSpan={2} className="text-left px-4 py-2 align-bottom">Agent</th>
                {metrics.map((m) => <th key={m} colSpan={3} className="text-center px-4 py-2 border-l border-slate-200">{METRIC_LABELS[m] || m}</th>)}
              </tr>
              <tr>
                {metrics.map((m) => (
                  <React.Fragment key={m}>
                    <th className="text-right px-3 py-2 border-l border-slate-200">Target</th>
                    <th className="text-right px-3 py-2">Achieved</th>
                    <th className="text-right px-3 py-2">%</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody data-testid="ta-agent-table">
              {agentRows.length === 0 && <tr><td colSpan={1 + metrics.length * 3} className="text-center py-10 text-slate-500">No data for {fmtMonthLabel(month)}.</td></tr>}
              {agentRows.map((r) => (
                <tr key={r.agent_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                  {metrics.map((m) => {
                    const target = r[`${m}_target`] || 0;
                    const achieved = r[`${m}_achieved`] || 0;
                    const pct = r[`${m}_pct`];
                    const colour = pct === null || pct === undefined ? "text-slate-400" : pct >= 100 ? "text-emerald-700" : pct >= 75 ? "text-amber-700" : "text-rose-600";
                    return (
                      <React.Fragment key={m}>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 border-l border-slate-100">{target.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{achieved.toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${colour}`}>{pct === null || pct === undefined ? "—" : `${pct}%`}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
